import React, { useEffect, useState } from 'react';
import { goalsApi } from '../api/client';
import type { Goals as GoalsType, Goal } from '../api/client';
import './Goals.css';

const Goals: React.FC = () => {
  const [goals, setGoals] = useState<GoalsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPhilosophy, setEditingPhilosophy] = useState(false);
  const [philosophyText, setPhilosophyText] = useState('');
  const [newGoal, setNewGoal] = useState({
    goal: '',
    type: 'shortTerm' as 'shortTerm' | 'longTerm',
    priority: 'medium' as 'high' | 'medium' | 'low',
    deadline: '',
    timeframe: ''
  });

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      setLoading(true);
      const response = await goalsApi.getGoals();
      setGoals(response.data);
      setPhilosophyText(response.data.philosophy || '');
    } catch (err) {
      setError('Failed to load goals');
      console.error('Goals error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddGoal = async () => {
    if (!newGoal.goal.trim()) return;

    try {
      const goalData = {
        goal: newGoal.goal,
        type: newGoal.type,
        priority: newGoal.priority,
        ...(newGoal.type === 'shortTerm' && newGoal.deadline && { deadline: newGoal.deadline }),
        ...(newGoal.type === 'longTerm' && newGoal.timeframe && { timeframe: newGoal.timeframe })
      };

      const response = await goalsApi.addGoal(goalData);
      setGoals(response.data);
      setShowAddModal(false);
      setNewGoal({
        goal: '',
        type: 'shortTerm',
        priority: 'medium',
        deadline: '',
        timeframe: ''
      });
    } catch (err) {
      console.error('Error adding goal:', err);
      setError('Failed to add goal');
    }
  };

  const handleDeleteGoal = async (goalId: string) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) return;

    try {
      await goalsApi.deleteGoal(goalId);
      await fetchGoals(); // Refresh goals after deletion
    } catch (err) {
      console.error('Error deleting goal:', err);
      setError('Failed to delete goal');
    }
  };

  const handleUpdatePhilosophy = async () => {
    try {
      const response = await goalsApi.updateGoals({ philosophy: philosophyText });
      setGoals(response.data);
      setEditingPhilosophy(false);
    } catch (err) {
      console.error('Error updating philosophy:', err);
      setError('Failed to update philosophy');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#27ae60';
      case 'in_progress': return '#3498db';
      case 'planning': return '#f39c12';
      default: return '#95a5a6';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#e74c3c';
      case 'medium': return '#f39c12';
      default: return '#95a5a6';
    }
  };

  const renderGoal = (goal: Goal, type: 'shortTerm' | 'longTerm') => (
    <div key={goal.id} className="goal-card">
      <div className="goal-header">
        <div className="goal-badges">
          <span 
            className="goal-priority-badge"
            style={{ backgroundColor: getPriorityColor(goal.priority) }}
          >
            {goal.priority}
          </span>
          <span 
            className="goal-status-badge"
            style={{ backgroundColor: getStatusColor(goal.status) }}
          >
            {goal.status.replace('_', ' ')}
          </span>
        </div>
        <button 
          className="goal-delete-btn"
          onClick={() => handleDeleteGoal(goal.id)}
          title="Delete goal"
        >
          ×
        </button>
      </div>
      
      <div className="goal-content">
        <h4 className="goal-text">{goal.goal}</h4>
        
        <div className="goal-details">
          {type === 'shortTerm' && goal.deadline && (
            <div className="goal-detail">
              <span className="detail-label">Deadline:</span>
              <span className="detail-value">{new Date(goal.deadline).toLocaleDateString()}</span>
            </div>
          )}
          {type === 'longTerm' && goal.timeframe && (
            <div className="goal-detail">
              <span className="detail-label">Timeframe:</span>
              <span className="detail-value">{goal.timeframe}</span>
            </div>
          )}
          <div className="goal-detail">
            <span className="detail-label">Created:</span>
            <span className="detail-value">{new Date(goal.createdAt).toLocaleDateString()}</span>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <div className="goals-page"><div className="loading">Loading goals...</div></div>;
  }

  if (error) {
    return <div className="goals-page"><div className="error">Error: {error}</div></div>;
  }

  return (
    <div className="goals-page">
      <header className="page-header">
        <h1>🎯 Goals</h1>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowAddModal(true)}
        >
          Add New Goal
        </button>
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="goals-content">
        {/* Philosophy Section */}
        <div className="philosophy-section">
          <div className="section-header">
            <h2>Philosophy</h2>
            <button 
              className="btn btn-secondary btn-small"
              onClick={() => setEditingPhilosophy(!editingPhilosophy)}
            >
              {editingPhilosophy ? 'Cancel' : 'Edit'}
            </button>
          </div>
          
          {editingPhilosophy ? (
            <div className="philosophy-edit">
              <textarea
                value={philosophyText}
                onChange={(e) => setPhilosophyText(e.target.value)}
                className="philosophy-textarea"
                rows={4}
                placeholder="Enter your personal philosophy..."
              />
              <button 
                className="btn btn-success btn-small"
                onClick={handleUpdatePhilosophy}
              >
                Save Philosophy
              </button>
            </div>
          ) : (
            <p className="philosophy-text">
              {goals?.philosophy || 'No philosophy set yet.'}
            </p>
          )}
        </div>

        {/* Goals Grid */}
        <div className="goals-grid">
          {/* Short-term Goals */}
          <div className="goals-column">
            <div className="column-header">
              <h2>Short-term Goals</h2>
              <span className="goal-count">{goals?.shortTerm?.length || 0}</span>
            </div>
            <div className="goals-list">
              {goals?.shortTerm?.map((goal) => renderGoal(goal, 'shortTerm'))}
              {(!goals?.shortTerm || goals.shortTerm.length === 0) && (
                <div className="no-goals">No short-term goals yet</div>
              )}
            </div>
          </div>

          {/* Long-term Goals */}
          <div className="goals-column">
            <div className="column-header">
              <h2>Long-term Goals</h2>
              <span className="goal-count">{goals?.longTerm?.length || 0}</span>
            </div>
            <div className="goals-list">
              {goals?.longTerm?.map((goal) => renderGoal(goal, 'longTerm'))}
              {(!goals?.longTerm || goals.longTerm.length === 0) && (
                <div className="no-goals">No long-term goals yet</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Goal Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Goal</h3>
              <button 
                className="modal-close"
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Goal Type</label>
                <select 
                  value={newGoal.type}
                  onChange={(e) => setNewGoal({...newGoal, type: e.target.value as 'shortTerm' | 'longTerm'})}
                  className="form-select"
                >
                  <option value="shortTerm">Short-term</option>
                  <option value="longTerm">Long-term</option>
                </select>
              </div>

              <div className="form-group">
                <label>Goal Description</label>
                <textarea
                  value={newGoal.goal}
                  onChange={(e) => setNewGoal({...newGoal, goal: e.target.value})}
                  className="form-textarea"
                  rows={3}
                  placeholder="Describe your goal..."
                />
              </div>

              <div className="form-group">
                <label>Priority</label>
                <select 
                  value={newGoal.priority}
                  onChange={(e) => setNewGoal({...newGoal, priority: e.target.value as 'high' | 'medium' | 'low'})}
                  className="form-select"
                >
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              {newGoal.type === 'shortTerm' ? (
                <div className="form-group">
                  <label>Deadline (optional)</label>
                  <input
                    type="date"
                    value={newGoal.deadline}
                    onChange={(e) => setNewGoal({...newGoal, deadline: e.target.value})}
                    className="form-input"
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label>Timeframe (optional)</label>
                  <input
                    type="text"
                    value={newGoal.timeframe}
                    onChange={(e) => setNewGoal({...newGoal, timeframe: e.target.value})}
                    className="form-input"
                    placeholder="e.g., 2-3 years, ongoing"
                  />
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                className="btn btn-success"
                onClick={handleAddGoal}
                disabled={!newGoal.goal.trim()}
              >
                Add Goal
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Goals;